// WP-0.7 (SE-7): privacy blur mode. One toggle hides all amounts; keyboard Ctrl+.
// The provider stamps the blur state onto a context; the Amount component reads it and
// sets data-privacy-blurred, which the global CSS blurs.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface PrivacyContextValue {
  blurred: boolean;
  toggle: () => void;
  setBlurred: (value: boolean) => void;
}

const PrivacyContext = createContext<PrivacyContextValue | null>(null);

export function PrivacyProvider({
  children,
  initialBlurred = false,
}: {
  children: React.ReactNode;
  initialBlurred?: boolean;
}): React.JSX.Element {
  const [blurred, setBlurred] = useState(initialBlurred);
  const toggle = useCallback(() => {
    setBlurred((b) => !b);
  }, []);

  // §18.1 keyboard: Ctrl+. toggles privacy blur globally.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key === '.') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [toggle]);

  const value = useMemo(() => ({ blurred, toggle, setBlurred }), [blurred, toggle]);
  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
}

export function usePrivacy(): PrivacyContextValue {
  const ctx = useContext(PrivacyContext);
  if (ctx === null) throw new Error('usePrivacy must be used within a PrivacyProvider');
  return ctx;
}

/** Lenient read for leaf components (e.g. Amount) that may render outside a provider. */
export function usePrivacyBlurred(): boolean {
  return useContext(PrivacyContext)?.blurred ?? false;
}
